import React, { useState, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useRealtimeNotifications } from '../hooks/useRealtimeNotifications';
import { useAuth, useLanguage, useOffline, useTheme } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  GraduationCap, LayoutDashboard, BookOpen, FileText, MessageSquare,
  CreditCard, Settings, User, LogOut, Bell, Menu, ChevronDown, ChevronRight,
  Building2, Users, Layers, BookMarked, Sparkles, Globe, Wifi, WifiOff,
  Home, Bot, Gift, Phone, DollarSign, ClipboardList, BarChart3,
  Calendar, PieChart, Award, BellRing, Monitor, RefreshCw, Search, Bus,
  Shield, Briefcase, FileCheck, Wallet, BookCheck, Library, UserCog, Clipboard,
  FolderOpen, UserCircle, Megaphone, HelpCircle, Inbox,
  Moon, Sun
} from 'lucide-react';

const LOGO_URL = 'https://customer-assets-eiarnc6j.emergentagent.net/job_uni-smartsys/artifacts/y8ickh5d_IMG-20260816-WA0087.jpg';

// Categorized navigation items by role
const getCategorizedNav = (role, t) => {
  const c = {
    dashboard: { id: 'dashboard', items: [
      { icon: LayoutDashboard, label: t('dashboard'), path: '/dashboard' },
    ]},
    structure: { id: 'structure', label: 'STRUCTURE', items: [
      { icon: Home, label: t('universities'), path: '/universities' },
      { icon: Building2, label: 'Organisation', path: '/organization' },
      { icon: Building2, label: t('faculties'), path: '/faculties' },
      { icon: Layers, label: t('departments'), path: '/departments' },
      { icon: BookMarked, label: t('programs'), path: '/programs' },
    ]},
    academic: { id: 'academic', label: 'ACADEMIQUE', items: [
      { icon: BookOpen, label: t('courses'), path: '/courses' },
      { icon: Monitor, label: 'Cours en Ligne', path: '/online-courses' },
      { icon: ClipboardList, label: 'Devoirs & TP', path: '/assignments' },
      { icon: FileText, label: t('grades'), path: '/grades' },
      { icon: RefreshCw, label: 'Gestion Academique', path: '/academic-year' },
      { icon: Calendar, label: 'Calendrier', path: '/schedule' },
      { icon: Award, label: 'Deliberations', path: '/deliberations' },
    ]},
    users: { id: 'users', label: 'UTILISATEURS', items: [
      { icon: Users, label: t('users'), path: '/users' },
      { icon: UserCog, label: 'Roles', path: '/roles' },
      { icon: FileCheck, label: 'Inscriptions', path: '/registrations' },
      { icon: Users, label: t('studyGroups') || 'Groupes', path: '/study-groups' },
    ]},
    communication: { id: 'communication', label: 'COMMUNICATION', items: [
      { icon: Megaphone, label: 'Annonces', path: '/announcements' },
      { icon: MessageSquare, label: t('messages'), path: '/messages' },
      { icon: Phone, label: t('calls') || 'Appels', path: '/calls' },
      { icon: BellRing, label: 'Notifications', path: '/notifications' },
    ]},
    finance: { id: 'finance', label: 'FINANCES', items: [
      { icon: CreditCard, label: t('payments'), path: '/payments' },
    ]},
    logistics: { id: 'logistics', label: 'LOGISTIQUE', items: [
      { icon: Bus, label: 'Transport', path: '/transport' },
      { icon: Library, label: t('library'), path: '/library' },
    ]},
    system: { id: 'system', label: 'SYSTEME', items: [
      { icon: Shield, label: 'Co-Admins', path: '/co-admins' },
      { icon: Bot, label: 'JosAide', path: '/ai-assistant' },
      { icon: Search, label: 'Dictionnaire', path: '/dictionary' },
      { icon: PieChart, label: 'Analytics', path: '/analytics' },
      { icon: Sparkles, label: 'Parrainage', path: '/referral' },
      { icon: Settings, label: t('settings'), path: '/settings' },
    ]},
    support: { id: 'support', label: 'SUPPORT', items: [
      { icon: HelpCircle, label: 'Aide & Support', path: '/support' },
      { icon: BookCheck, label: 'Guide Interactif', path: '/guide' },
      { icon: Inbox, label: 'Demandes Collaboration', path: '/collaboration-inbox' },
    ]},
    profile: { id: 'profile', items: [
      { icon: User, label: t('profile'), path: '/profile' },
    ]},
    dossiers: { id: 'dossiers', label: 'DOSSIERS', items: [
      { icon: FolderOpen, label: 'Mes Dossiers', path: '/my-documents' },
    ]},
    teacherProfile: { id: 'teacherProfile', label: 'ENSEIGNANT', items: [
      { icon: UserCircle, label: 'Profil Enseignant', path: '/teacher-profile' },
    ]},
    classManagement: { id: 'classManagement', label: 'CLASSES', items: [
      { icon: Layers, label: 'Gestion Classes', path: '/class-groups' },
    ]},
    myClass: { id: 'myClass', label: 'MA CLASSE', items: [
      { icon: Layers, label: 'Ma Classe', path: '/my-class' },
    ]},
  };

  // Helper: filter items by paths
  const pick = (cat, paths) => ({ ...cat, items: cat.items.filter(i => paths.includes(i.path)) });

  switch (role) {
    // ===== PLATFORM =====
    case 'super_admin':
      return [c.dashboard, c.structure, c.academic, c.classManagement, c.users, c.communication, c.finance, c.logistics, c.system, c.support, c.profile];
    case 'co_admin':
      return [c.dashboard, c.structure, c.academic, c.classManagement,
        pick(c.users, ['/users', '/registrations']), c.communication, c.finance, c.logistics,
        pick(c.system, ['/ai-assistant', '/analytics', '/settings']),
        pick(c.support, ['/support', '/guide', '/collaboration-inbox']), c.profile];

    // ===== UNIVERSITY MANAGEMENT =====
    case 'rector':
      return [c.dashboard, 
        pick(c.structure, ['/organization', '/faculties', '/departments', '/programs']),
        c.academic, c.classManagement, c.users, c.communication, c.finance, c.logistics,
        pick(c.system, ['/ai-assistant', '/analytics', '/settings']),
        pick(c.support, ['/support', '/guide']), c.profile];
    case 'vice_rector_academic':
      return [c.dashboard,
        pick(c.structure, ['/faculties', '/departments', '/programs']),
        c.academic, c.classManagement, pick(c.users, ['/users']), c.communication, 
        pick(c.logistics, ['/library']),
        pick(c.system, ['/ai-assistant', '/analytics']),
        pick(c.support, ['/support', '/guide']), c.profile];
    case 'sg_academic':
      return [c.dashboard,
        pick(c.structure, ['/faculties', '/departments', '/programs']),
        c.academic, c.classManagement, pick(c.users, ['/users', '/registrations']), c.communication,
        pick(c.logistics, ['/library']),
        pick(c.system, ['/ai-assistant', '/analytics']),
        pick(c.support, ['/support', '/guide']), c.profile];
    case 'sg_admin':
      return [c.dashboard,
        pick(c.structure, ['/organization']),
        pick(c.users, ['/users', '/roles']), c.communication,
        pick(c.system, ['/settings']),
        pick(c.support, ['/support', '/guide']), c.profile];
    case 'sg_research':
      return [c.dashboard,
        pick(c.structure, ['/programs']),
        pick(c.academic, ['/courses']),
        pick(c.users, ['/users']), 
        pick(c.communication, ['/messages']),
        pick(c.system, ['/ai-assistant', '/analytics']),
        pick(c.support, ['/support', '/guide']), c.profile];
    case 'budget_director':
      return [c.dashboard, c.finance,
        pick(c.communication, ['/messages']),
        pick(c.system, ['/analytics']),
        pick(c.support, ['/support', '/guide']), c.profile];

    // ===== FACULTY =====
    case 'dean':
      return [c.dashboard,
        pick(c.structure, ['/organization', '/departments', '/programs']),
        c.academic, c.classManagement, pick(c.users, ['/users']), c.communication,
        pick(c.logistics, ['/library']),
        pick(c.system, ['/ai-assistant', '/analytics']),
        pick(c.support, ['/support', '/guide']), c.profile];
    case 'vice_dean':
      return [c.dashboard,
        pick(c.structure, ['/programs']),
        pick(c.academic, ['/courses', '/grades', '/schedule', '/assignments', '/deliberations']),
        pick(c.users, ['/users']), pick(c.communication, ['/messages']),
        pick(c.logistics, ['/library']),
        pick(c.support, ['/support', '/guide']), c.profile];
    case 'faculty_secretary':
      return [c.dashboard,
        pick(c.academic, ['/courses', '/grades', '/schedule', '/assignments']),
        pick(c.users, ['/users', '/registrations']),
        pick(c.communication, ['/messages']),
        pick(c.support, ['/support', '/guide']), c.profile];

    // ===== DEPARTMENT =====
    case 'department_head':
      return [c.dashboard,
        pick(c.structure, ['/programs']),
        pick(c.academic, ['/courses', '/grades', '/schedule', '/assignments', '/deliberations']),
        c.classManagement,
        pick(c.users, ['/users']), pick(c.communication, ['/messages']),
        pick(c.logistics, ['/library']),
        pick(c.system, ['/ai-assistant', '/analytics']),
        pick(c.support, ['/support', '/guide']), c.profile];
    case 'section_head':
      return [c.dashboard,
        pick(c.academic, ['/courses', '/grades', '/schedule']),
        pick(c.users, ['/users']),
        pick(c.communication, ['/messages']), c.profile];
    case 'dept_secretary':
      return [c.dashboard,
        pick(c.academic, ['/courses', '/schedule']),
        pick(c.users, ['/users']),
        pick(c.communication, ['/messages']), c.profile];

    // ===== TEACHING =====
    case 'teacher':
    case 'visiting_teacher':
      return [c.dashboard,
        pick(c.academic, ['/courses', '/online-courses', '/assignments', '/grades', '/academic-year', '/schedule', '/deliberations']),
        c.teacherProfile,
        c.communication, pick(c.logistics, ['/library']),
        pick(c.system, ['/ai-assistant', '/dictionary']),
        pick(c.support, ['/support', '/guide']), c.profile];

    // ===== ADMIN STAFF =====
    case 'registrar':
      return [c.dashboard,
        pick(c.users, ['/users', '/registrations']),
        pick(c.academic, ['/courses']),
        pick(c.communication, ['/messages']), c.profile];
    case 'cashier':
      return [c.dashboard, c.finance,
        pick(c.communication, ['/messages']), c.profile];
    case 'librarian':
      return [c.dashboard,
        pick(c.logistics, ['/library']),
        pick(c.users, ['/users']),
        pick(c.communication, ['/messages']), c.profile];
    case 'it_admin':
      return [c.dashboard,
        pick(c.structure, ['/organization']),
        pick(c.users, ['/users', '/roles']),
        pick(c.communication, ['/messages']),
        pick(c.system, ['/settings', '/analytics']), c.profile];
    case 'apparitor':
      return [c.dashboard,
        pick(c.academic, ['/courses', '/schedule']),
        pick(c.communication, ['/messages']), c.profile];
    case 'protocol_chief':
      return [c.dashboard,
        pick(c.communication, ['/messages', '/notifications']),
        c.profile];
    case 'receptionist':
      return [c.dashboard,
        pick(c.communication, ['/messages']),
        c.profile];
    case 'admin_staff':
      return [c.dashboard,
        pick(c.users, ['/users']),
        pick(c.communication, ['/messages']),
        pick(c.system, ['/settings']), c.profile];
    case 'academic_secretary':
      return [c.dashboard,
        pick(c.academic, ['/courses', '/grades']),
        pick(c.users, ['/users', '/registrations']),
        pick(c.communication, ['/messages']), c.profile];

    // ===== TRANSPORT =====
    case 'driver':
      return [c.dashboard,
        { id: 'transport', label: 'TRANSPORT', items: [
          { icon: Bus, label: 'Transport', path: '/transport' },
        ]},
        pick(c.communication, ['/messages', '/notifications']),
        c.profile];

    // ===== STUDENT =====
    case 'student':
    default:
      return [c.dashboard,
        pick(c.academic, ['/courses', '/online-courses', '/assignments', '/grades', '/academic-year', '/schedule', '/deliberations']),
        c.myClass,
        c.dossiers,
        { ...pick(c.communication, ['/announcements', '/messages', '/notifications']), items: [
          ...c.communication.items.filter(i => ['/announcements', '/messages', '/notifications'].includes(i.path)),
          { icon: Users, label: t('studyGroups') || 'Groupes', path: '/study-groups' },
        ]},
        c.finance, c.logistics,
        pick(c.system, ['/ai-assistant', '/dictionary']),
        pick(c.support, ['/support', '/guide']),
        { ...c.profile, items: [
          ...c.profile.items,
          { icon: Sparkles, label: 'Parrainage', path: '/referral' },
        ]},
      ];
  }
};

// Mobile bottom nav
const getMobileNavItems = (role, t) => {
  const base = [
    { icon: Home, label: t('dashboard'), path: '/dashboard' },
  ];
  const menuBtn = { icon: Menu, label: 'Menu', path: '__menu__' };

  switch (role) {
    case 'student':
      return [...base, 
        { icon: BookOpen, label: t('courses'), path: '/courses' },
        { icon: MessageSquare, label: t('messages'), path: '/messages' },
        { icon: Bus, label: 'Transport', path: '/transport' },
        menuBtn];
    case 'teacher':
    case 'visiting_teacher':
      return [...base,
        { icon: BookOpen, label: t('courses'), path: '/courses' },
        { icon: FileText, label: t('grades'), path: '/grades' },
        { icon: MessageSquare, label: t('messages'), path: '/messages' },
        menuBtn];
    case 'driver':
      return [...base,
        { icon: Bus, label: 'Transport', path: '/transport' },
        { icon: MessageSquare, label: t('messages'), path: '/messages' },
        menuBtn];
    case 'cashier':
      return [...base,
        { icon: CreditCard, label: t('payments'), path: '/payments' },
        { icon: MessageSquare, label: t('messages'), path: '/messages' },
        menuBtn];
    case 'librarian':
      return [...base,
        { icon: Library, label: t('library'), path: '/library' },
        { icon: MessageSquare, label: t('messages'), path: '/messages' },
        menuBtn];
    default:
      return [...base,
        { icon: Users, label: t('users'), path: '/users' },
        { icon: MessageSquare, label: t('messages'), path: '/messages' },
        { icon: BellRing, label: 'Notifs', path: '/notifications' },
        menuBtn];
  }
};

// Sidebar Content Component (reused in desktop and mobile sheet)
const SidebarContent = ({ categories, location, onLinkClick, user, t, language, handleLogout }) => {
  const userInitials = user ? `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase() : 'U';
  const [collapsed, setCollapsed] = useState({});

  const toggleGroup = useCallback((id) => {
    setCollapsed(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  // Auto-expand group containing active path
  const activeGroup = categories.find(cat => cat.items?.some(i => i.path === location.pathname));

  return (
    <div className="flex flex-col h-full bg-white dark:bg-slate-800">
      {/* Logo */}
      <div className="p-5 border-b border-slate-100 dark:border-slate-700">
        <Link to="/dashboard" className="flex items-center gap-3" onClick={onLinkClick} data-testid="sidebar-logo">
          <img src={LOGO_URL} alt="JoSmart Uni" className="w-10 h-10 rounded-xl object-cover" />
          <div>
            <h2 className="font-heading font-bold text-[#0F2C59] dark:text-white text-base leading-tight">JoSmart Uni</h2>
          </div>
        </Link>
      </div>

      {/* Nav Items */}
      <ScrollArea className="flex-1 py-2">
        <nav className="px-3 space-y-0.5">
          {categories.map((cat, ci) => {
            const isCollapsed = collapsed[cat.id] && !(activeGroup?.id === cat.id);
            const hasLabel = !!cat.label;

            return (
              <div key={cat.id + ci}>
                {hasLabel ? (
                  <button
                    onClick={() => toggleGroup(cat.id)}
                    className="w-full flex items-center justify-between mt-4 mb-1 px-3 py-1.5 rounded-lg hover:bg-slate-50 transition-colors group"
                    data-testid={`sidebar-group-${cat.id}`}
                  >
                    <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400 group-hover:text-slate-600 transition-colors">
                      {cat.label}
                    </p>
                    <ChevronRight className={`w-3 h-3 text-slate-300 transition-transform duration-200 ${isCollapsed ? '' : 'rotate-90'}`} />
                  </button>
                ) : null}
                {!isCollapsed && cat.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={onLinkClick}
                      className={`group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-150 ${
                        isActive
                          ? 'bg-[#F0F7FF] text-[#007BFF] font-semibold border-r-[3px] border-[#007BFF]'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                      }`}
                      data-testid={`sidebar-${item.path.replace('/', '')}`}
                    >
                      <Icon className={`w-[18px] h-[18px] flex-shrink-0 ${isActive ? 'text-[#007BFF]' : 'text-slate-400 group-hover:text-slate-600'}`} />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            );
          })}
        </nav>
      </ScrollArea>

      {/* Footer - User */}
      <div className="border-t border-slate-100 p-4">
        <div className="flex items-center gap-3">
          <Avatar className="w-9 h-9">
            <AvatarFallback className="bg-[#0F2C59] text-white text-xs font-semibold">
              {userInitials}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900 truncate">{user?.first_name} {user?.last_name}</p>
            <p className="text-xs text-slate-500 truncate">{t(user?.role || 'student')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <Button variant="ghost" size="sm" onClick={() => window.location.href='/profile'} className="flex-1 text-xs text-slate-600 h-8" data-testid="sidebar-profile-btn">
            <User className="w-3.5 h-3.5 mr-1" /> Profil
          </Button>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="flex-1 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 h-8" data-testid="logout-button">
            <LogOut className="w-3.5 h-3.5 mr-1" /> {t('logout')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { t, language, changeLanguage } = useLanguage();
  const { isOnline, pendingCount } = useOffline();
  const { darkMode, toggleDarkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { unreadCount, notifications: realtimeNotifs, markRead, markAllRead } = useRealtimeNotifications(true);

  const categories = getCategorizedNav(user?.role, t);
  const mobileNavItems = getMobileNavItems(user?.role, t);

  const handleLogout = () => { logout(); navigate('/login'); };
  const userInitials = user ? `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase() : 'U';

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-slate-900" data-testid="dashboard-layout">
      {/* ===== DESKTOP SIDEBAR ===== */}
      <aside className="hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:left-0 lg:w-64 bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 z-40 shadow-sm">
        <SidebarContent
          categories={categories}
          location={location}
          onLinkClick={() => {}}
          user={user}
          t={t}
          language={language}
          handleLogout={handleLogout}
        />
      </aside>

      {/* ===== TOP HEADER ===== */}
      <header className="sticky top-0 z-30 lg:ml-64 h-16 bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl border-b border-slate-200 dark:border-slate-700 flex items-center justify-between px-4 lg:px-8">
        {/* Left: Mobile hamburger + page context */}
        <div className="flex items-center gap-3">
          <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" className="w-9 h-9" data-testid="mobile-menu-button">
                <Menu className="w-5 h-5 text-slate-700" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0">
              <SidebarContent
                categories={categories}
                location={location}
                onLinkClick={() => setSidebarOpen(false)}
                user={user}
                t={t}
                language={language}
                handleLogout={handleLogout}
              />
            </SheetContent>
          </Sheet>

          {/* Mobile Logo */}
          <Link to="/dashboard" className="lg:hidden flex items-center gap-2" data-testid="header-logo-mobile">
            <img src={LOGO_URL} alt="JoSmart Uni" className="w-8 h-8 rounded-lg object-cover" />
            <span className="font-heading font-bold text-[#0F2C59] text-sm">JoSmart Uni</span>
          </Link>
        </div>

        {/* Right: Status + Lang + Notifs + User */}
        <div className="flex items-center gap-1.5 sm:gap-2">
          {/* Online/Offline */}
          <div className={`hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium ${
            isOnline ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
          }`} data-testid="online-status">
            {isOnline ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
            <span>{isOnline ? t('online') : t('offline')}</span>
            {pendingCount > 0 && (
              <Badge className="bg-orange-500 text-white text-[9px] h-4 px-1.5 ml-0.5">{pendingCount}</Badge>
            )}
          </div>

          {/* Language */}
          <Button
            variant="ghost" size="sm"
            onClick={() => changeLanguage(language === 'fr' ? 'en' : 'fr')}
            className="h-9 px-2.5 text-slate-600 dark:text-slate-300 hover:text-slate-900 gap-1.5"
            data-testid="language-toggle"
          >
            <Globe className="w-4 h-4" />
            <span className="text-xs font-semibold uppercase">{language}</span>
          </Button>

          {/* Dark Mode */}
          <Button
            variant="ghost" size="icon"
            onClick={toggleDarkMode}
            className="h-9 w-9 text-slate-600 dark:text-slate-300"
            data-testid="dark-mode-toggle"
          >
            {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>

          {/* Notifications */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="relative h-9 w-9" data-testid="notifications-button">
                <Bell className="w-[18px] h-[18px] text-slate-600 dark:text-slate-300" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 rounded-full flex items-center justify-center text-[10px] text-white font-bold px-1" data-testid="notif-badge">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <div className="flex items-center justify-between px-3 py-2">
                <DropdownMenuLabel className="p-0">{t('notifications')}</DropdownMenuLabel>
                {unreadCount > 0 && (
                  <button onClick={markAllRead} className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                    {language === 'fr' ? 'Tout marquer lu' : 'Mark all read'}
                  </button>
                )}
              </div>
              <DropdownMenuSeparator />
              {realtimeNotifs.length === 0 ? (
                <div className="p-4 text-center text-sm text-slate-500">
                  {language === 'fr' ? 'Aucune notification' : 'No notifications'}
                </div>
              ) : (
                <div className="max-h-64 overflow-y-auto">
                  {realtimeNotifs.slice(0, 10).map(n => (
                    <DropdownMenuItem key={n.id} className={`px-3 py-2.5 cursor-pointer ${!n.is_read ? 'bg-blue-50/50 dark:bg-blue-900/20' : ''}`} onClick={() => markRead(n.id)}>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm truncate ${!n.is_read ? 'font-semibold text-slate-800' : 'text-slate-600'}`}>{n.title || n.message}</p>
                        {n.data?.author_name && <p className="text-xs text-slate-400 mt-0.5">Par {n.data.author_name}</p>}
                      </div>
                      {!n.is_read && <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 ml-2" />}
                    </DropdownMenuItem>
                  ))}
                </div>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild className="justify-center">
                <Link to="/notifications" className="text-sm text-blue-600 font-medium">
                  {language === 'fr' ? 'Voir tout' : 'See all'}
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* User Avatar */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-9 px-2 gap-2" data-testid="user-menu">
                <Avatar className="w-8 h-8">
                  <AvatarFallback className="bg-[#0F2C59] text-white text-xs font-semibold">
                    {userInitials}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden md:block text-sm font-medium text-slate-700">{user?.first_name}</span>
                <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <p className="font-semibold text-slate-900">{user?.first_name} {user?.last_name}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/profile" className="flex items-center gap-2">
                  <User className="w-4 h-4" /> {t('profile')}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/settings" className="flex items-center gap-2">
                  <Settings className="w-4 h-4" /> {t('settings')}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="header-logout">
                <LogOut className="w-4 h-4 mr-2" /> {t('logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* ===== MAIN CONTENT ===== */}
      <main className="lg:ml-64 pb-20 lg:pb-8">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8 dark:text-white">
          {children}
        </div>
      </main>

      {/* ===== MOBILE BOTTOM NAV ===== */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-slate-200 z-40 pwa-padding-bottom shadow-[0_-2px_10px_rgba(0,0,0,0.05)]" data-testid="mobile-bottom-nav">
        <div className="flex items-center justify-around h-16">
          {mobileNavItems.map((item) => {
            const Icon = item.icon;
            if (item.path === '__menu__') {
              return (
                <button
                  key="menu"
                  onClick={() => setSidebarOpen(true)}
                  className="flex flex-col items-center justify-center flex-1 h-full text-slate-500 active:text-[#007BFF]"
                  data-testid="mobile-nav-menu"
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-[10px] mt-0.5 font-medium">{item.label}</span>
                </button>
              );
            }
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
                  isActive ? 'text-[#007BFF]' : 'text-slate-500'
                }`}
                data-testid={`mobile-nav-${item.path.replace('/', '')}`}
              >
                <Icon className={`w-5 h-5 ${isActive ? 'text-[#007BFF]' : ''}`} />
                <span className={`text-[10px] mt-0.5 font-medium ${isActive ? 'text-[#007BFF]' : ''}`}>{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
};
